import React, { useEffect, useMemo, useState } from "react";
import {
  FieldType,
  FilterOperator,
  getOperatorsForFieldType,
  isAbsoluteDateOperator,
  isAllOperator,
  isRelativeDateOperator,
  ReportFilterV2,
} from "@/common/report-configuration";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, Loader2 } from "lucide-react";
import { FieldOption, useFieldOptions } from "../../../hooks/useFieldOptions";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  formatDate,
  fromLocalCalendarDate,
  toLocalCalendarDate,
  toPlainDate,
  todayUTC,
} from "@/common/dateUtils";
import type { ISODate } from "@/common/dateUtils";
import { DatasetName } from "@/common/constants";
import { FILTERS } from "@/common/filters";
import { Calendar } from "@/components/ui/calendar";

interface FilterDropdownProps {
  displayName: string;
  V2Filter: ReportFilterV2;
  onFilterChange: (filter: ReportFilterV2) => void;
  datasetName: DatasetName;
  defaultOpen?: boolean;
  disabled?: boolean;
}

type TypedValue<T extends FieldType> = T extends "string" | "special:string"
  ? string[]
  : T extends "number" | "special:number"
    ? number | [number, number]
    : T extends "boolean"
      ? boolean
      : T extends "date"
        ? ISODate
        : never;

const getTypedValue = <T extends FieldType>(
  value: ReportFilterV2["value"],
  type: T,
): TypedValue<T> => {
  if (value === undefined || value === null || value === "") {
    switch (type) {
      case "string":
      case "special:string":
        return [] as string[] as TypedValue<T>;
      case "number":
      case "special:number":
        return 0 as number as TypedValue<T>;
      case "boolean":
        return false as boolean as TypedValue<T>;
      case "date":
        return todayUTC().toString() as TypedValue<T>;
      default:
        throw new Error(`Unsupported type: ${type}`);
    }
  }

  switch (type) {
    case "string":
    case "special:string":
      return (Array.isArray(value)
        ? value.map(String)
        : [String(value)]) as string[] as TypedValue<T>;
    case "number":
    case "special:number":
      if (Array.isArray(value)) {
        return value.map(Number) as [number, number] as TypedValue<T>;
      }
      return Number(value) as number as TypedValue<T>;
    case "boolean":
      return Boolean(value) as boolean as TypedValue<T>;
    case "date":
      return (toPlainDate(value) ?? todayUTC()).toString() as TypedValue<T>;
    default:
      throw new Error(`Unsupported type: ${type}`);
  }
};

// These helper functions are now imported from report-configuration

// Local helpers to safely work with date input values without breaking on partial input
const toISODateString = (val: unknown): string => {
  return toPlainDate(val)?.toString() ?? "";
};

const toCalendarDate = (value: unknown): Date | undefined => {
  const isoDate = toPlainDate(value)?.toString();
  return isoDate ? toLocalCalendarDate(isoDate) : undefined;
};

const isCompleteDateRange = (
  startValue: unknown,
  endValue: unknown,
): boolean => {
  const startDate = toPlainDate(startValue);
  const endDate = toPlainDate(endValue);
  return Boolean(
    startDate && endDate && startDate.toString() <= endDate.toString(),
  );
};

const toNumberInputString = (val: unknown): string => {
  if (val === undefined || val === null || val === "") return "";
  const n = Number(val);
  return Number.isFinite(n) ? String(n) : "";
};

const getStringArrayValue = (value: ReportFilterV2["value"]): string[] => {
  if (Array.isArray(value)) {
    return value.map(String);
  }

  if (value === undefined || value === null || value === "") {
    return [];
  }

  return [String(value)];
};

const getSingleStringValue = (value: ReportFilterV2["value"]): string => {
  if (Array.isArray(value)) {
    return String(value[0] ?? "");
  }

  if (value === undefined || value === null) {
    return "";
  }

  return String(value);
};

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  displayName,
  V2Filter,
  onFilterChange,
  datasetName,
  defaultOpen = false,
  disabled = false,
}) => {
  const filterDefinition = useMemo(
    () => FILTERS[V2Filter.filterDefinitionName],
    [V2Filter.filterDefinitionName],
  );
  const filterType = filterDefinition.type;
  const filterField = filterDefinition.field;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [localFilter, setLocalFilter] = useState<ReportFilterV2>(V2Filter);
  const [searchTerm, setSearchTerm] = useState("");
  const [numberInputs, setNumberInputs] = useState<{
    primary: string;
    secondary: string;
  }>({
    primary: toNumberInputString(V2Filter.value),
    secondary: toNumberInputString(V2Filter.secondaryValue),
  });

  const { options, loading, error } = useFieldOptions(
    datasetName,
    V2Filter.filterDefinitionName,
  );

  // Sync local state when the filter prop changes externally
  useEffect(() => {
    setLocalFilter(V2Filter);
  }, [V2Filter]);

  useEffect(() => {
    if (filterType !== "number" && filterType !== "special:number") return;
    setNumberInputs({
      primary: toNumberInputString(localFilter.value),
      secondary: toNumberInputString(localFilter.secondaryValue),
    });
  }, [
    filterType,
    localFilter.operator,
    localFilter.value,
    localFilter.secondaryValue,
  ]);

  const handleApply = () => {
    if (!canApply) return;
    onFilterChange(localFilter);
    setIsOpen(false);
  };

  const canApply =
    filterType !== "date" ||
    localFilter.operator !== "BETWEEN" ||
    isCompleteDateRange(localFilter.value, localFilter.secondaryValue);

  const operators = getOperatorsForFieldType(filterType);

  const renderOperatorSelect = () => {
    if (operators.length <= 1) return null;

    return (
      <div className="space-y-2">
        <Select
          value={localFilter.operator as string}
          onValueChange={(value: string) => {
            const operator = value as FilterOperator;
            let nextValue: ReportFilterV2["value"] = getTypedValue(
              localFilter.value,
              filterType,
            );

            if (filterType === "string" || filterType === "special:string") {
              nextValue =
                operator === "CONTAINS"
                  ? getSingleStringValue(localFilter.value)
                  : getStringArrayValue(localFilter.value);
            }

            setLocalFilter({
              ...localFilter,
              operator,
              value: nextValue,
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select operator" />
          </SelectTrigger>
          <SelectContent>
            {operators.map((op) => (
              <SelectItem key={op.value} value={op.value}>
                {op.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  const renderStringFilterValue = () => {
    if (localFilter.operator === "CONTAINS") {
      const value = getSingleStringValue(localFilter.value).trim();

      if (value.length === 0) {
        return null;
      }

      return (
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-gray-600 truncate">{value}</span>
        </div>
      );
    }

    if (!Array.isArray(localFilter.value) || localFilter.value.length === 0) {
      return null;
    }

    return (
      <div className="flex items-center gap-1 min-w-0">
        <span
          className={`text-gray-600 truncate ${localFilter.operator === "NOT_EQUALS" ? "line-through" : ""}`}
        >
          {localFilter.value.length <= 2
            ? localFilter.value.join(", ")
            : `${localFilter.value[0]}, ${localFilter.value[1]}`}
        </span>
        {localFilter.value.length > 2 && (
          <div className="bg-gray-200 dark:bg-gray-700 rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center text-xs shrink-0">
            {localFilter.value.length}
          </div>
        )}
      </div>
    );
  };

  /**
   * Select types are a special case of string filters that only allow a single value and have a predefined set of options, so we display them differently in the dropdown trigger.
   */
  const renderSelectFilterValue = () => {
    if (options.length === 0) {
      return null;
    }
    const selectedOption = options.find(
      (option: FieldOption) => option.value === String(localFilter.value),
    );
    if (!selectedOption) {
      return null;
    }
    return (
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-gray-600 truncate">{selectedOption.label}</span>
      </div>
    );
  };

  const renderDateFilterValue = () => {
    const dateOperator = localFilter.operator;

    if (dateOperator === "ALL") {
      return "All Time";
    }

    if (isRelativeDateOperator(dateOperator)) {
      return operators.find((op) => op.value === dateOperator)?.label;
    }

    if (dateOperator === "BETWEEN") {
      const start = toISODateString(localFilter.value);
      const end = toISODateString(localFilter.secondaryValue);
      return `${start ? formatDate(start) : "Start date"} - ${
        end ? formatDate(end) : "End date"
      }`;
    }

    if (isAbsoluteDateOperator(dateOperator)) {
      return `${operators.find((op) => op.value === dateOperator)?.label} ${formatDate(
        toISODateString(localFilter.value),
      )}`;
    }

    return formatDate(toISODateString(localFilter.value));
  };

  const renderNumberFilterValue = () => {
    if (localFilter.operator === "BETWEEN") {
      const primaryValue = Array.isArray(localFilter.value)
        ? localFilter.value[0]
        : localFilter.value;
      const secondaryValue = Array.isArray(localFilter.value)
        ? localFilter.value[1]
        : localFilter.secondaryValue;
      return `${primaryValue} - ${secondaryValue}`;
    }
    return (
      <span
        className={localFilter.operator === "NOT_EQUALS" ? "line-through" : ""}
      >
        {String(localFilter.value as number)}
      </span>
    );
  };

  const renderValueInput = () => {
    switch (filterType) {
      case "select":
        return (
          <Select
            value={String(localFilter.value)}
            onValueChange={(value) =>
              setLocalFilter({
                ...localFilter,
                value,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "string":
      case "special:string": {
        const selectedValues = getStringArrayValue(localFilter.value);
        const filteredOptions = options.filter((option) =>
          option.value.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        if (localFilter.operator === "CONTAINS") {
          return (
            <Input
              type="text"
              value={getSingleStringValue(localFilter.value)}
              onChange={(e) =>
                setLocalFilter({
                  ...localFilter,
                  value: e.target.value,
                })
              }
              placeholder="Enter text to match"
            />
          );
        }

        if (
          localFilter.operator === "EQUALS" ||
          localFilter.operator === "NOT_EQUALS"
        ) {
          return (
            <div className="space-y-2">
              <Select
                value={
                  Array.isArray(selectedValues)
                    ? selectedValues[0] || ""
                    : selectedValues || ""
                }
                onValueChange={(value: string) =>
                  setLocalFilter({ ...localFilter, value: [value] })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a value" />
                </SelectTrigger>
                <SelectContent>
                  {filteredOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }

        const handleSelectAll = () => {
          setLocalFilter({
            ...localFilter,
            value: options.map((option) => option.value),
          });
        };

        const handleUnselectAll = () => {
          setLocalFilter({ ...localFilter, value: [] });
        };

        return (
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
            <div className="flex gap-2 text-xs">
              <button
                onClick={handleSelectAll}
                className="text-blue-500 hover:text-blue-700"
              >
                Select All
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={handleUnselectAll}
                className="text-blue-500 hover:text-blue-700"
              >
                Unselect All
              </button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : error ? (
              <div className="text-red-500 text-sm py-2">{error}</div>
            ) : (
              <ScrollArea className="h-[200px] pr-4">
                <div className="space-y-2">
                  {filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`${filterField}-${option.value}`}
                        checked={selectedValues.includes(option.value)}
                        onCheckedChange={(checked) => {
                          const newValues = checked
                            ? [...selectedValues, option.value]
                            : selectedValues.filter((v) => v !== option.value);
                          setLocalFilter({
                            ...localFilter,
                            value: newValues,
                          });
                        }}
                      />
                      <label
                        htmlFor={`${filterField}-${option.value}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                  {filteredOptions.length === 0 && (
                    <div className="text-sm text-gray-500 py-2">
                      No options found
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        );
      }

      case "number":
      case "special:number":
        if (localFilter.operator === "BETWEEN") {
          const currentValue = getTypedValue(localFilter.value, "number");
          const secondaryValue =
            typeof localFilter.secondaryValue === "number"
              ? localFilter.secondaryValue
              : 0;
          const values = Array.isArray(currentValue)
            ? currentValue
            : [currentValue, secondaryValue];

          return (
            <div className="space-y-2">
              <Input
                type="number"
                value={numberInputs.primary}
                onChange={(e) => {
                  const raw = e.target.value;
                  setNumberInputs((prev) => ({ ...prev, primary: raw }));
                  if (raw === "") return;
                  const value = Number(raw);
                  if (Number.isNaN(value)) return;
                  setLocalFilter({
                    ...localFilter,
                    value: value,
                    secondaryValue: values[1],
                  });
                }}
                placeholder="From"
              />
              <Input
                type="number"
                value={numberInputs.secondary}
                onChange={(e) => {
                  const raw = e.target.value;
                  setNumberInputs((prev) => ({ ...prev, secondary: raw }));
                  if (raw === "") return;
                  const value = Number(raw);
                  if (Number.isNaN(value)) return;
                  setLocalFilter({
                    ...localFilter,
                    value: values[0],
                    secondaryValue: value,
                  });
                }}
                placeholder="To"
              />
            </div>
          );
        }

        return (
          <Input
            type="number"
            value={numberInputs.primary}
            onChange={(e) => {
              const raw = e.target.value;
              setNumberInputs((prev) => ({ ...prev, primary: raw }));
              if (raw === "") return;
              const value = Number(raw);
              if (Number.isNaN(value)) return;
              setLocalFilter({
                ...localFilter,
                value,
              });
            }}
          />
        );

      case "date": {
        const dateOperator = localFilter.operator;

        if (dateOperator === "ALL" || isRelativeDateOperator(dateOperator)) {
          return null;
        }

        if (dateOperator === "BETWEEN") {
          const selectedRange = {
            from: toCalendarDate(localFilter.value),
            to: toCalendarDate(localFilter.secondaryValue),
          };

          return (
            <div className="flex justify-center">
              <Calendar
                mode="range"
                selected={selectedRange}
                resetOnSelect
                onSelect={(range) =>
                  setLocalFilter({
                    ...localFilter,
                    value: range?.from
                      ? (fromLocalCalendarDate(
                          range.from,
                        ).toString() as ISODate)
                      : "",
                    secondaryValue: range?.to
                      ? (fromLocalCalendarDate(range.to).toString() as ISODate)
                      : undefined,
                  })
                }
              />
            </div>
          );
        }
        const selectedDate = toCalendarDate(localFilter.value);
        return (
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (!date) return;
                setLocalFilter({
                  ...localFilter,
                  value: fromLocalCalendarDate(date).toString() as ISODate,
                  secondaryValue: undefined,
                });
              }}
            />
          </div>
        );
      }

      case "boolean":
        return (
          <Select
            value={String(localFilter.value)}
            onValueChange={(value) =>
              setLocalFilter({
                ...localFilter,
                value: value === "true",
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        );

      default:
        return null;
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={disabled ? undefined : setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between text-left border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
          disabled={disabled}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold whitespace-nowrap">
              {displayName}
            </span>
            {filterType === "select" && renderSelectFilterValue()}
            {filterType === "string" && renderStringFilterValue()}
            {filterType === "special:string" && renderStringFilterValue()}
            {filterType === "date" && (
              <span className="text-gray-600 truncate">
                {renderDateFilterValue()}
              </span>
            )}
            {filterType === "number" && (
              <span className="text-gray-600 truncate">
                {renderNumberFilterValue()}
              </span>
            )}
            {filterType === "special:number" && (
              <span className="text-gray-600 truncate">
                {renderNumberFilterValue()}
              </span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      {!disabled && (
        <PopoverContent align="start" className="w-80">
          <div className="space-y-4">
            {renderOperatorSelect()}
            {(filterType !== "date" ||
              (filterType === "date" &&
                !isAllOperator(localFilter.operator))) && (
              <div className="space-y-2 mx-2">
                {operators.length > 1 &&
                  !isRelativeDateOperator(localFilter.operator) && (
                    <Label>Value</Label>
                  )}
                {renderValueInput()}
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button disabled={!canApply} onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};
